import { useMemo, useState } from "react";
import {
  applyPromiseAction,
  createPromise,
  type PromiseOverview,
} from "../application/round03-service";
import type { CEOOSDatabase } from "../db/database";
import { dateKeyForInstant } from "../domain/business-date";
import type {
  CEOProfile,
  CounterpartyType,
  PromiseEntry,
  PromiseMateriality,
  PromiseStatus,
} from "../domain/types";
import { safeErrorMessage } from "../privacy/policy";
import { GovernanceTabs, type GovernanceArea } from "../ui/GovernanceTabs";

const STATUS_LABELS: Record<PromiseStatus, string> = {
  draft: "草稿",
  active: "履约中",
  fulfilled: "已兑现",
  renegotiation_needed: "需要重谈",
  renegotiated: "已重谈",
  overdue: "已逾期",
  cancelled: "已取消",
  breached: "已违约",
  archived: "已归档",
};

const MATERIALITY_LABELS: Record<PromiseMateriality, string> = {
  low: "低",
  medium: "中",
  high: "高",
  critical: "关键",
};

function localDateTimeValue(iso: string): string {
  const date = new Date(iso);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function PromiseDetail({
  database,
  promise,
  onClose,
  onSaved,
}: {
  database: CEOOSDatabase;
  promise: PromiseEntry;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [mode, setMode] = useState<"renegotiate" | "fulfill" | "cancel" | "breach" | undefined>();
  const [note, setNote] = useState("");
  const [nextDueAt, setNextDueAt] = useState(localDateTimeValue(promise.dueAt));
  const [notice, setNotice] = useState("");
  const terminal = ["fulfilled", "cancelled", "breached", "archived"].includes(
    promise.promiseStatus,
  );

  const runAction = async () => {
    if (!mode) return;
    try {
      const result = await applyPromiseAction(
        database,
        promise.id,
        mode === "renegotiate"
          ? { type: "renegotiate", nextDueAt: new Date(nextDueAt).toISOString(), note }
          : mode === "fulfill"
            ? { type: "fulfill", evidence: note }
            : mode === "cancel"
              ? { type: "cancel", reason: note }
              : { type: "breach", reason: note },
      );
      if (result.issues.some((item) => item.severity === "blocking")) {
        setNotice(result.issues.map((item) => `${item.code}：${item.message}`).join("；"));
        return;
      }
      setNotice(
        result.issues.length
          ? result.issues.map((item) => `${item.code}：${item.message}`).join("；")
          : "承诺状态已保存，原始截止时间与历史没有被覆盖。",
      );
      setMode(undefined);
      setNote("");
      await onSaved();
    } catch (error) {
      setNotice(safeErrorMessage(error));
    }
  };

  return (
    <main className="governance-page">
      <header className="governance-header">
        <button type="button" className="text-button" onClick={onClose}>
          ← 返回承诺账本
        </button>
        <span className="eyebrow">Promise detail</span>
        <h1>{promise.content}</h1>
        <p>
          对手方别名：{promise.counterpartyAlias} · {MATERIALITY_LABELS[promise.materiality]}重要性
        </p>
      </header>
      {notice && (
        <div className="inline-notice" role="status">
          {notice}
        </div>
      )}
      <section className="promise-detail-grid">
        <div>
          <span>状态</span>
          <strong>{STATUS_LABELS[promise.promiseStatus]}</strong>
        </div>
        <div>
          <span>当前截止</span>
          <strong>{new Date(promise.dueAt).toLocaleString("zh-CN")}</strong>
        </div>
        <div>
          <span>原始截止（不可覆盖）</span>
          <strong>{new Date(promise.originalDueAt).toLocaleString("zh-CN")}</strong>
        </div>
        <div>
          <span>信任影响</span>
          <strong>{promise.trustImpact}</strong>
        </div>
      </section>

      {!terminal && (
        <section className="governance-section" aria-labelledby="promise-actions">
          <h2 id="promise-actions">状态动作</h2>
          <div className="segmented-actions">
            <button type="button" onClick={() => setMode("renegotiate")}>
              重谈截止
            </button>
            <button type="button" onClick={() => setMode("fulfill")}>
              标记兑现
            </button>
            <button type="button" onClick={() => setMode("cancel")}>
              取消
            </button>
            <button type="button" onClick={() => setMode("breach")}>
              标记违约
            </button>
          </div>
          {mode && (
            <div className="governance-form action-form">
              {mode === "renegotiate" && (
                <label className="field">
                  <span>双方确认后的新截止时间</span>
                  <input
                    type="datetime-local"
                    value={nextDueAt}
                    onChange={(event) => setNextDueAt(event.target.value)}
                  />
                </label>
              )}
              <label className="field">
                <span>
                  {mode === "fulfill"
                    ? "最小可观察证据（建议填写）"
                    : mode === "renegotiate"
                      ? "重谈说明（必填）"
                      : mode === "cancel"
                        ? "取消原因（必填）"
                        : "区别于普通逾期的信任损害事实（必填）"}
                </span>
                <textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
              </label>
              <button
                type="button"
                className="primary-button"
                disabled={mode !== "fulfill" && !note.trim()}
                onClick={() => void runAction()}
              >
                确认状态动作
              </button>
            </div>
          )}
        </section>
      )}

      <section className="governance-list" aria-labelledby="promise-history">
        <div className="section-title-row">
          <h2 id="promise-history">不可变历史</h2>
          <span>{promise.history.length}</span>
        </div>
        {promise.history
          .slice()
          .reverse()
          .map((item) => (
            <div className="governance-row governance-row--static" key={item.id}>
              <span>
                <strong>{item.action}</strong>
                <small>
                  {new Date(item.occurredAt).toLocaleString("zh-CN")}
                  {item.note ? ` · ${item.note}` : ""}
                </small>
              </span>
            </div>
          ))}
      </section>
    </main>
  );
}

function PromiseSection({
  id,
  title,
  items,
  onSelect,
}: {
  id: string;
  title: string;
  items: PromiseEntry[];
  onSelect: (id: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <section className="governance-list" aria-labelledby={id}>
      <div className="section-title-row">
        <h2 id={id}>{title}</h2>
        <span>{items.length}</span>
      </div>
      {items.map((promise) => (
        <button
          type="button"
          className="governance-row"
          key={promise.id}
          onClick={() => onSelect(promise.id)}
        >
          <span>
            <strong>{promise.content}</strong>
            <small>
              {promise.counterpartyAlias} · {new Date(promise.dueAt).toLocaleString("zh-CN")} ·{" "}
              {STATUS_LABELS[promise.promiseStatus]}
            </small>
          </span>
          <span className={`status-dot status-dot--${promise.promiseStatus}`} aria-hidden="true" />
        </button>
      ))}
    </section>
  );
}

export function PromiseLedger({
  database,
  overview,
  profile,
  onAreaChange,
  onRefresh,
}: {
  database: CEOOSDatabase;
  overview: PromiseOverview;
  profile: CEOProfile;
  onAreaChange: (area: GovernanceArea) => void;
  onRefresh: () => Promise<void>;
}) {
  const [openedAtMs] = useState(() => Date.now());
  const defaultDue = new Date(openedAtMs + 24 * 60 * 60 * 1000).toISOString();
  const [activeId, setActiveId] = useState<string>();
  const [counterpartyType, setCounterpartyType] = useState<CounterpartyType>("team");
  const [counterpartyAlias, setCounterpartyAlias] = useState("");
  const [content, setContent] = useState("");
  const [dueAt, setDueAt] = useState(localDateTimeValue(defaultDue));
  const [materiality, setMateriality] = useState<PromiseMateriality>("medium");
  const [trustImpact, setTrustImpact] = useState("");
  const [notice, setNotice] = useState("");
  const selected = useMemo(
    () => overview.promises.find((promise) => promise.id === activeId),
    [activeId, overview.promises],
  );

  if (selected) {
    return (
      <PromiseDetail
        database={database}
        promise={selected}
        onClose={() => setActiveId(undefined)}
        onSaved={onRefresh}
      />
    );
  }

  const submit = async () => {
    try {
      const promisedAt = new Date().toISOString();
      await createPromise(database, {
        effectiveDate: dateKeyForInstant(promisedAt, profile.businessTimezone),
        counterpartyType,
        counterpartyAlias: counterpartyAlias.trim(),
        content: content.trim(),
        promisedAt,
        dueAt: new Date(dueAt).toISOString(),
        businessTimezone: profile.businessTimezone,
        materiality,
        trustImpact: trustImpact.trim(),
        confidentiality: "sensitive",
      });
      setCounterpartyAlias("");
      setContent("");
      setTrustImpact("");
      setDueAt(localDateTimeValue(defaultDue));
      setNotice("承诺已进入本地账本；不会自动通知对手方或董事会。");
      await onRefresh();
    } catch (error) {
      setNotice(safeErrorMessage(error));
    }
  };

  const ungrouped = overview.promises.filter(
    (promise) =>
      !overview.todayDue.includes(promise) &&
      !overview.dueSoon.includes(promise) &&
      !overview.overdue.includes(promise) &&
      !overview.renegotiationNeeded.includes(promise) &&
      !overview.fulfilled.includes(promise) &&
      !overview.breached.includes(promise),
  );

  return (
    <main className="governance-page">
      <header className="governance-header">
        <span className="brand">CEO OS</span>
        <h1>承诺账本</h1>
        <p>把承诺、截止与状态变化放到同一条可追溯时间线上。</p>
      </header>
      <GovernanceTabs area="promise" onChange={onAreaChange} />
      {notice && (
        <div className="inline-notice" role="status">
          {notice}
        </div>
      )}

      {overview.topRisk && (
        <section className="risk-rail">
          <span className="eyebrow">最高承诺风险</span>
          <h2>{overview.topRisk.content}</h2>
          <p>
            {overview.topRisk.counterpartyAlias} · 截止{" "}
            {new Date(overview.topRisk.dueAt).toLocaleString("zh-CN")} ·{" "}
            {STATUS_LABELS[overview.topRisk.promiseStatus]}
          </p>
        </section>
      )}

      <details className="governance-disclosure">
        <summary>记录一个承诺</summary>
        <div className="governance-form">
          <div className="two-column-fields">
            <label className="field">
              <span>对手方类型</span>
              <select
                value={counterpartyType}
                onChange={(event) => setCounterpartyType(event.target.value as CounterpartyType)}
              >
                <option value="client">客户</option>
                <option value="partner">合作伙伴</option>
                <option value="team">团队</option>
                <option value="supplier">供应商</option>
                <option value="institution">机构</option>
                <option value="self">自己</option>
                <option value="other">其他</option>
              </select>
            </label>
            <label className="field">
              <span>对手方别名</span>
              <input
                value={counterpartyAlias}
                onChange={(event) => setCounterpartyAlias(event.target.value)}
              />
            </label>
          </div>
          <label className="field">
            <span>承诺内容</span>
            <textarea
              rows={3}
              value={content}
              onChange={(event) => setContent(event.target.value)}
            />
          </label>
          <div className="two-column-fields">
            <label className="field">
              <span>截止时间</span>
              <input
                type="datetime-local"
                value={dueAt}
                onChange={(event) => setDueAt(event.target.value)}
              />
            </label>
            <label className="field">
              <span>重要性</span>
              <select
                value={materiality}
                onChange={(event) => setMateriality(event.target.value as PromiseMateriality)}
              >
                {Object.entries(MATERIALITY_LABELS).map(([value, label]) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="field">
            <span>若未兑现，会伤害什么信任</span>
            <input value={trustImpact} onChange={(event) => setTrustImpact(event.target.value)} />
          </label>
          <button
            type="button"
            className="primary-button"
            disabled={
              !counterpartyAlias.trim() ||
              !content.trim() ||
              !trustImpact.trim() ||
              !dueAt ||
              new Date(dueAt).getTime() <= openedAtMs
            }
            onClick={() => void submit()}
          >
            保存到本地账本
          </button>
        </div>
      </details>

      {overview.promises.length === 0 ? (
        <div className="empty-state">
          <strong>当前没有承诺记录</strong>
          <p>系统不会根据一句话自动制造承诺。</p>
        </div>
      ) : (
        <>
          <PromiseSection
            id="promise-today"
            title="今天到期"
            items={overview.todayDue}
            onSelect={setActiveId}
          />
          <PromiseSection
            id="promise-overdue"
            title="已逾期（不等于违约）"
            items={overview.overdue}
            onSelect={setActiveId}
          />
          <PromiseSection
            id="promise-renegotiation"
            title="需要重谈"
            items={overview.renegotiationNeeded}
            onSelect={setActiveId}
          />
          <PromiseSection
            id="promise-soon"
            title="七天内到期"
            items={overview.dueSoon.filter((item) => !overview.todayDue.includes(item))}
            onSelect={setActiveId}
          />
          <PromiseSection
            id="promise-open"
            title="其他履约中"
            items={ungrouped}
            onSelect={setActiveId}
          />
          <PromiseSection
            id="promise-fulfilled"
            title="已兑现"
            items={overview.fulfilled}
            onSelect={setActiveId}
          />
          <PromiseSection
            id="promise-breached"
            title="已违约"
            items={overview.breached}
            onSelect={setActiveId}
          />
        </>
      )}
    </main>
  );
}
